import { useQuery } from "@tanstack/react-query";
import BadgeAwarenessCard from "components/badge-awareness/badge-awareness-card.component";
import DeepDiveSideData from "screens/careers/DeepDiveSideData";
import React from "react";
import { getUserBadgesReport } from "services/ranking.service";
import { BADGE_SUBTEXT } from "constants/badge-subtext";
import { Badge, IUserBadge } from "interfaces/badge.interface";

const GlimpseRankings: React.FC = () => {
  const { data: userBadgeReport } = useQuery({
    queryKey: ["userBadgeReport"],
    queryFn: getUserBadgesReport,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  // Pick first not-yet-earned badge tier; fall back to last when all earned
  const currentBadge =
    userBadgeReport?.badges?.find(
      (b: Badge) => b.preview_percentage < 100 || b.deeper_dive_percentage < 100,
    ) ||
    userBadgeReport?.badges?.[userBadgeReport.badges.length - 1];

  const sideProgressPercentage = currentBadge?.preview_percentage ?? 0;

  const previewCenterData = `${currentBadge?.completed_preview_count ?? 0}/${
    currentBadge?.preview_count ?? 0
  }`;

  const deeperDivePercentage = currentBadge?.deeper_dive_percentage ?? 0;

  const deeperdiveCenterData = `${
    currentBadge?.completed_deeper_dive_count ?? 0
  }/${currentBadge?.deeper_dive_count ?? 0}`;

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:items-baseline">
      {/* Left column: Earned Badges + Career Awareness Levels */}
      <div className="flex-1 min-w-0 flex flex-col gap-8">
        <div>
          <div className="text-lg font-semibold leading-6 text-brand">
            Earned Badges
          </div>
          <div className="flex items-center py-3">
            {userBadgeReport && userBadgeReport.user_badges.length === 0 ? (
              <div className="text-sm text-text-muted">
                You haven't earned any badges yet.
              </div>
            ) : (
              userBadgeReport &&
              userBadgeReport.user_badges.map((item: IUserBadge, index: number) => (
                <div className="block p-4" key={index}>
                  <div className="flex flex-col justify-center">
                    <img
                      src={`/assets/svg/badge-${item.slug}.svg`}
                      alt="badge"
                      className="flex min-w-[100px] w-[200px] h-auto pl-2"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="pb-4 text-lg font-semibold leading-6 text-brand">
            Career Awareness Levels
          </div>
          <div className="flex flex-col gap-[20px]">
            {userBadgeReport &&
              userBadgeReport.badges?.map((item: Badge, index: number) => (
                <BadgeAwarenessCard
                  key={index}
                  iconSlug={item.slug}
                  title={item.title}
                  subtext={BADGE_SUBTEXT[item.slug] || ""}
                  progress={[
                    {
                      label: `${item.completed_preview_count}/${item.preview_count} Previews`,
                      percent: item.preview_percentage,
                    },
                    {
                      label: `${item.completed_deeper_dive_count}/${item.deeper_dive_count} Deeper Dives`,
                      percent: item.deeper_dive_percentage,
                    },
                  ]}
                />
              ))}
          </div>
        </div>
      </div>

      {/* Right column: donut overview, top-aligned with Earned Badges */}
      <div className="lg:w-[280px] lg:min-w-[250px] shrink-0 self-start">
        {userBadgeReport && (
          <DeepDiveSideData
            showTopThree={false}
            showCareerAwareness={false}
            externalData={{
              videoProgressPercentage: sideProgressPercentage,
              previewCenterData: `${previewCenterData}`,
              deeperDivePercentage,
              deeperdiveCenterData,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default GlimpseRankings;
